import { applicationsAPI } from "../services/api";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaShieldHalved,
  FaCircleCheck,
  FaPlane,
  FaCalendarDays,
  FaPenToSquare,
  FaFileArrowUp,
  FaClipboardList,
  FaArrowRight,
  FaArrowLeft,
  FaCircleQuestion,
  FaHeadphones,
  FaChevronDown,
  FaCheck,
  FaEnvelope,
} from "react-icons/fa6";
import CircularBadge from "../components/badge";
import "./ApplyForm.css";

const ease = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  { num: 1, key: "identity", label: "Who You Are", sub: "Identity", Icon: FaUser },
  { num: 2, key: "service", label: "Your Service", sub: "Service Information", Icon: FaShieldHalved },
  { num: 3, key: "eligibility", label: "Eligibility Check", sub: "Important Questions", Icon: FaCircleQuestion },
  { num: 4, key: "logistics", label: "Logistics", sub: "Travel & Contacts", Icon: FaPlane },
  { num: 5, key: "availability", label: "Availability", sub: "When You Can Come", Icon: FaCalendarDays },
  { num: 6, key: "story", label: "Your Story", sub: "Your Journey", Icon: FaPenToSquare },
  { num: 7, key: "documents", label: "Documents", sub: "Uploads", Icon: FaFileArrowUp },
  { num: 8, key: "review", label: "Review", sub: "Review & Submit", Icon: FaClipboardList },
];
const TOTAL = CHAPTERS.length;

/* ── Field helpers — all accept error prop ── */
function Field({ label, required, half, error, children }) {
  return (
    <div className={`apf__field ${half ? "apf__field--half" : ""} ${error ? "apf__field--error" : ""}`}>
      <label className="apf__label">
        {label}
        {required && (
          <span className="apf__req" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {children}
      {error && <p className="apf__field-error-msg">{error}</p>}
    </div>
  );
}
function Input({ error, ...props }) {
  return <input className={`apf__input${error ? " apf__input--error" : ""}`} {...props} />;
}
function Textarea({ rows = 5, error, ...props }) {
  return <textarea className={`apf__input apf__textarea${error ? " apf__input--error" : ""}`} rows={rows} {...props} />;
}
function Select({ children, error, ...props }) {
  return (
    <div className="apf__select-wrap">
      <select className={`apf__input apf__select${error ? " apf__input--error" : ""}`} {...props}>
        {children}
      </select>
      <FaChevronDown className="apf__select-icon" aria-hidden="true" />
    </div>
  );
}
function RadioGroup({ name, options, value, onChange, error }) {
  return (
    <div className={`apf__radios${error ? " apf__radios--error" : ""}`}>
      {options.map((opt) => (
        <label key={opt.value} className={`apf__radio${value === opt.value ? " apf__radio--active" : ""}`}>
          <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} />
          <span className="apf__radio-mark" aria-hidden="true" />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

/* ── Validation — returns {field: "message"} ── */
function getErrors(key, data) {
  const e = {};
  if (key === "identity") {
    if (!data.firstName?.trim()) e.firstName = "First name is required.";
    if (!data.lastName?.trim()) e.lastName = "Last name is required.";
    if (!data.email?.trim()) e.email = "Email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = "Please enter a valid email address.";
    if (!data.phone?.trim()) e.phone = "Phone number is required.";
    if (!data.dob) e.dob = "Date of birth is required.";
    if (!data.country) e.country = "Country is required.";
    if (!data.state) e.state = "State / Province is required.";
    if (!data.city?.trim()) e.city = "City is required.";
    if (!data.zip?.trim()) e.zip = "Zip / Postal code is required.";
  }
  if (key === "service") {
    if (!data.group) e.group = "Please select a category.";
    if ((data.group === "first_responder" || data.group === "law_enforcement") && !data.agency?.trim()) e.agency = "Agency / Department is required.";
    if (data.group === "gold_star" && !data.relationship) e.relationship = "Relationship is required.";
  }
  if (key === "eligibility") {
    if (!data.suicidal) e.suicidal = "Please answer this question.";
    if (!data.addiction) e.addiction = "Please answer this question.";
  }
  if (key === "logistics") {
    if (!data.passport) e.passport = "Passport status is required.";
    if (!data.ecName?.trim()) e.ecName = "Emergency contact name is required.";
    if (!data.ecPhone?.trim()) e.ecPhone = "Emergency contact phone is required.";
    if (!data.ecRel) e.ecRel = "Relationship is required.";
  }
  if (key === "availability") {
    if (!data.season) e.season = "Please select a preferred timeframe.";
    if (!data.international) e.international = "Please answer this question.";
  }
  if (key === "story") {
    if (!data.why?.trim()) e.why = "Please share why you are applying.";
    else if (data.why.trim().length < 100) e.why = `${100 - data.why.trim().length} more characters needed (minimum 100).`;
    if (!data.hope?.trim()) e.hope = "Please share what you hope to gain.";
  }
  return e;
}

/* ── Chapter 1 ── */
function Ch1Identity({ data, set, errors }) {
  return (
    <>
      <div className="apf__row">
        <Field label="First Name" required half error={errors.firstName}>
          <Input placeholder="Enter your first name" value={data.firstName || ""} onChange={(e) => set("firstName", e.target.value)} error={errors.firstName} />
        </Field>
        <Field label="Last Name" required half error={errors.lastName}>
          <Input placeholder="Enter your last name" value={data.lastName || ""} onChange={(e) => set("lastName", e.target.value)} error={errors.lastName} />
        </Field>
      </div>
      <div className="apf__row">
        <Field label="Preferred Name (if different)" half>
          <Input placeholder="Enter preferred name" value={data.preferred || ""} onChange={(e) => set("preferred", e.target.value)} />
        </Field>
        <Field label="Email Address" required half error={errors.email}>
          <Input type="email" placeholder="youremail@example.com" value={data.email || ""} onChange={(e) => set("email", e.target.value)} error={errors.email} />
        </Field>
      </div>
      <div className="apf__row">
        <Field label="Phone Number" required half error={errors.phone}>
          <Input type="tel" placeholder="+1 (555) 123-4567" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} error={errors.phone} />
        </Field>
        <Field label="Date of Birth" required half error={errors.dob}>
          <Input type="date" value={data.dob || ""} onChange={(e) => set("dob", e.target.value)} error={errors.dob} />
        </Field>
      </div>
      <div className="apf__row">
        <Field label="Gender (Optional)" half>
          <Select value={data.gender || ""} onChange={(e) => set("gender", e.target.value)}>
            <option value="">Select an option</option>
            <option>Male</option>
            <option>Female</option>
            {/* <option>Non-binary</option>
            <option>Prefer not to say</option> */}
          </Select>
        </Field>
        <Field label="Country" required half error={errors.country}>
          <Select value={data.country || "Select a country"} onChange={(e) => set("country", e.target.value)} error={errors.country}>
            <option>Select a country</option>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="UK">United Kingdom</option>
            <option value="other">Other</option>
          </Select>
        </Field>
      </div>
      <div className="apf__row">
        <Field label="State / Province" required half error={errors.state}>
          <Select value={data.state || ""} onChange={(e) => set("state", e.target.value)} error={errors.state}>
            <option value="">Select your state</option>
            {[
              "AL",
              "AK",
              "AZ",
              "AR",
              "CA",
              "CO",
              "CT",
              "DE",
              "FL",
              "GA",
              "HI",
              "ID",
              "IL",
              "IN",
              "IA",
              "KS",
              "KY",
              "LA",
              "ME",
              "MD",
              "MA",
              "MI",
              "MN",
              "MS",
              "MO",
              "MT",
              "NE",
              "NV",
              "NH",
              "NJ",
              "NM",
              "NY",
              "NC",
              "ND",
              "OH",
              "OK",
              "OR",
              "PA",
              "RI",
              "SC",
              "SD",
              "TN",
              "TX",
              "UT",
              "VT",
              "VA",
              "WA",
              "WV",
              "WI",
              "WY",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </Field>
        <Field label="City" required half error={errors.city}>
          <Input placeholder="Enter your city" value={data.city || ""} onChange={(e) => set("city", e.target.value)} error={errors.city} />
        </Field>
      </div>
      <Field label="Zip / Postal Code" required error={errors.zip}>
        <Input placeholder="Enter zip / postal code" value={data.zip || ""} onChange={(e) => set("zip", e.target.value)} error={errors.zip} style={{ maxWidth: "20rem" }} />
      </Field>
    </>
  );
}

/* ── Chapter 2 ── */
function Ch2Service({ data, set, errors }) {
  return (
    <>
      <Field label="Which group best describes you?" required error={errors.group}>
        <RadioGroup
          name="group"
          value={data.group || ""}
          onChange={(v) => set("group", v)}
          error={errors.group}
          options={[
            { value: "veteran", label: "Veteran" },
            { value: "first_responder", label: "First Responder" },
            { value: "law_enforcement", label: "Law Enforcement" },
            { value: "gold_star", label: "Gold Star Family" },
          ]}
        />
      </Field>
      {data.group === "veteran" && (
        <>
          <div className="apf__row">
            <Field label="Military Branch" half>
              <Select value={data.branch || ""} onChange={(e) => set("branch", e.target.value)}>
                <option value="">Select branch</option>
                {["Army", "Navy", "Air Force", "Marine Corps", "Coast Guard", "Space Force", "National Guard"].map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </Select>
            </Field>
            <Field label="Rank (Optional)" half>
              <Input placeholder="e.g. Sergeant, Captain" value={data.rank || ""} onChange={(e) => set("rank", e.target.value)} />
            </Field>
          </div>
          <Field label="Service Dates">
            <Input placeholder="e.g. 2005 – 2012" value={data.serviceDates || ""} onChange={(e) => set("serviceDates", e.target.value)} />
          </Field>
        </>
      )}
      {(data.group === "first_responder" || data.group === "law_enforcement") && (
        <Field label="Agency / Department" required error={errors.agency}>
          <Input placeholder="Enter your agency or department" value={data.agency || ""} onChange={(e) => set("agency", e.target.value)} error={errors.agency} />
        </Field>
      )}
      {data.group === "gold_star" && (
        <Field label="Relationship to Fallen Service Member" required error={errors.relationship}>
          <Select value={data.relationship || ""} onChange={(e) => set("relationship", e.target.value)} error={errors.relationship}>
            <option value="">Select relationship</option>
            {["Spouse / Partner", "Parent", "Child", "Sibling", "Other"].map((r) => (
              <option key={r}>{r}</option>
            ))}
          </Select>
        </Field>
      )}
      <Field label="Current Employment Status">
        <RadioGroup
          name="employment"
          value={data.employment || ""}
          onChange={(v) => set("employment", v)}
          options={[
            { value: "yes", label: "Employed" },
            { value: "no", label: "Not Employed" },
            { value: "retired", label: "Retired" },
          ]}
        />
      </Field>
    </>
  );
}

/* ── Chapter 3 ── */
function Ch3Eligibility({ data, set, errors, onCrisis }) {
  const answered = data.suicidal !== undefined && data.addiction !== undefined;
  const crisis = data.suicidal === "yes" || data.addiction === "yes";
  const handle = (field, val) => {
    set(field, val);
    if (val === "yes") onCrisis();
  };
  return (
    <div className="apf__elig">
      <div className="apf__elig-intro">
        <FaShieldHalved className="apf__elig-icon" aria-hidden="true" />
        <p>To ensure this program is the right fit, we ask two important questions before continuing. Your answers are confidential.</p>
      </div>
      <div className={`apf__elig-q${errors.suicidal ? " apf__elig-q--error" : ""}`}>
        <div className="apf__elig-q-text">
          <FaCircleQuestion aria-hidden="true" />
          <p>Are you currently experiencing active suicidal thoughts?</p>
        </div>
        <div className="apf__elig-btns">
          <button type="button" className={`apf__elig-btn${data.suicidal === "yes" ? " yes" : ""}`} onClick={() => handle("suicidal", "yes")}>
            YES
          </button>
          <button type="button" className={`apf__elig-btn${data.suicidal === "no" ? " no" : ""}`} onClick={() => set("suicidal", "no")}>
            NO
          </button>
        </div>
        {errors.suicidal && <p className="apf__elig-error">{errors.suicidal}</p>}
      </div>
      <div className={`apf__elig-q${errors.addiction ? " apf__elig-q--error" : ""}`}>
        <div className="apf__elig-q-text">
          <FaCircleQuestion aria-hidden="true" />
          <p>Are you currently experiencing active addiction or substance use that would make participation unsafe?</p>
        </div>
        <div className="apf__elig-btns">
          <button type="button" className={`apf__elig-btn${data.addiction === "yes" ? " yes" : ""}`} onClick={() => handle("addiction", "yes")}>
            YES
          </button>
          <button type="button" className={`apf__elig-btn${data.addiction === "no" ? " no" : ""}`} onClick={() => set("addiction", "no")}>
            NO
          </button>
        </div>
        {errors.addiction && <p className="apf__elig-error">{errors.addiction}</p>}
      </div>
      {crisis && (
        <div className="apf__crisis">
          <FaShieldHalved aria-hidden="true" />
          <div>
            <p className="apf__crisis-title">You Are Not Alone</p>
            <p>
              We will share immediate resources and a member of our team will reach out to you personally. Please call or text <strong>988</strong> (Suicide & Crisis Lifeline) right now if you need
              immediate support.
            </p>
            <a href="tel:988" className="apf__crisis-link">
              Call 988 Now →
            </a>
          </div>
        </div>
      )}
      {answered && !crisis && (
        <div className="apf__elig-ok">
          <FaCircleCheck aria-hidden="true" />
          <p>Thank you. You may continue with your application.</p>
        </div>
      )}
    </div>
  );
}

/* ── Chapter 4 ── */
function Ch4Logistics({ data, set, errors }) {
  return (
    <>
      <p className="apf__section-label">Traveling From</p>
      <div className="apf__row">
        <Field label="City" half>
          <Input placeholder="Your city" value={data.travelCity || ""} onChange={(e) => set("travelCity", e.target.value)} />
        </Field>
        <Field label="State" half>
          <Input placeholder="Your state / province" value={data.travelState || ""} onChange={(e) => set("travelState", e.target.value)} />
        </Field>
      </div>
      <Field label="Country">
        <Select value={data.travelCountry || "US"} onChange={(e) => set("travelCountry", e.target.value)}>
          <option value="US">United States</option>
          <option value="CA">Canada</option>
          <option value="UK">United Kingdom</option>
          <option value="other">Other</option>
        </Select>
      </Field>
      <Field label="Nearest Airport / Airport Preference">
        <Input placeholder="e.g. PHX, DEN, LAX" value={data.airport || ""} onChange={(e) => set("airport", e.target.value)} />
      </Field>
      <p className="apf__section-label">Emergency Contact</p>
      <div className="apf__row">
        <Field label="Full Name" required half error={errors.ecName}>
          <Input placeholder="Emergency contact name" value={data.ecName || ""} onChange={(e) => set("ecName", e.target.value)} error={errors.ecName} />
        </Field>
        <Field label="Phone Number" required half error={errors.ecPhone}>
          <Input type="tel" placeholder="+1 (555) 000-0000" value={data.ecPhone || ""} onChange={(e) => set("ecPhone", e.target.value)} error={errors.ecPhone} />
        </Field>
      </div>
      <Field label="Relationship" required error={errors.ecRel}>
        <Select value={data.ecRel || ""} onChange={(e) => set("ecRel", e.target.value)} error={errors.ecRel}>
          <option value="">Select relationship</option>
          {["Spouse / Partner", "Parent", "Sibling", "Friend", "Other"].map((r) => (
            <option key={r}>{r}</option>
          ))}
        </Select>
      </Field>
      <Field label="Passport Status" required error={errors.passport}>
        <RadioGroup
          name="passport"
          value={data.passport || ""}
          onChange={(v) => set("passport", v)}
          error={errors.passport}
          options={[
            { value: "have", label: "I have one" },
            { value: "none", label: "I don't have one" },
            { value: "applying", label: "Currently applying" },
          ]}
        />
      </Field>
    </>
  );
}

/* ── Chapter 5 ── */
function Ch5Availability({ data, set, errors }) {
  return (
    <>
      <Field label="Preferred Timeframe" required error={errors.season}>
        <RadioGroup
          name="season"
          value={data.season || ""}
          onChange={(v) => set("season", v)}
          error={errors.season}
          options={[
            { value: "asap", label: "As soon as possible" },
            { value: "3_6mo", label: "3–6 months" },
            { value: "6_12mo", label: "6–12 months" },
            { value: "undecided", label: "Undecided" },
          ]}
        />
      </Field>
      <Field label="Can you travel internationally?" required error={errors.international}>
        <RadioGroup
          name="international"
          value={data.international || ""}
          onChange={(v) => set("international", v)}
          error={errors.international}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
        />
      </Field>
      <Field label="Any scheduling limitations or blackout dates?">
        <Textarea placeholder="Let us know if there are specific dates or periods you're unavailable..." value={data.limitations || ""} onChange={(e) => set("limitations", e.target.value)} rows={3} />
      </Field>
      <Field label="Room Preference">
        <RadioGroup
          name="room"
          value={data.room || ""}
          onChange={(v) => set("room", v)}
          options={[
            { value: "shared", label: "Shared" },
            { value: "private", label: "Private if Available" },
            { value: "none", label: "No Preference" },
          ]}
        />
      </Field>
    </>
  );
}

/* ── Chapter 6 ── */
function Ch6Story({ data, set, errors }) {
  const count1 = (data.why || "").length,
    count2 = (data.hope || "").length;
  return (
    <>
      <Field label="Why are you applying?" required error={errors.why}>
        <Textarea
          placeholder="Share what brings you to Forge. There's no wrong answer here. (100 characters minimum)"
          value={data.why || ""}
          onChange={(e) => set("why", e.target.value)}
          rows={6}
          error={errors.why}
        />
        <span className={`apf__char-count${count1 < 100 ? " apf__char-count--warn" : ""}`}>{count1} / 100 minimum</span>
      </Field>
      <Field label="What do you hope to gain from this experience?" required error={errors.hope}>
        <Textarea placeholder="Tell us what healing, growth, or change looks like for you..." value={data.hope || ""} onChange={(e) => set("hope", e.target.value)} rows={5} error={errors.hope} />
        <span className="apf__char-count">{count2} characters</span>
      </Field>
      <Field label="Is there anything else you'd like us to know?">
        <Textarea placeholder="Additional context, questions, or anything else on your mind..." value={data.extra || ""} onChange={(e) => set("extra", e.target.value)} rows={4} />
      </Field>
    </>
  );
}

/* ── Chapter 7 ── */
const ELIGIBILITY_DOC_TYPES = [
  "Government/department-issued service ID or credentials",
  "DD214/DD1300 or equivalent government service record",
  "Official employment/separation/retirement letter from the agency",
  "State professional certification/license showing the qualifying occupation, preferably with evidence of actual agency service",
  "Pension/retirement documentation",
  "Other official documentation approved by Forge after review",
];

function Ch7Documents({ data, set }) {
  const serviceLabel = "Official documentation verifying your service or eligibility";
  const [showDocInfo, setShowDocInfo] = useState(false);
  return (
    <div className="apf__docs">
      <p className="apf__docs-note">
        Upload your documents below. Accepted formats: PDF, JPG, PNG (max 10MB each). You can also email them to <a href="mailto:info@forge.ngo">info@forge.ngo</a> after submitting.
      </p>
      {["Government Photo ID *", serviceLabel + " *"].map((label, i) => (
        <div key={i} className="apf__upload">
          <p className="apf__upload-label">
            {label}
            {i === 1 && (
              <button type="button" className="apf__doc-info-btn" onClick={() => setShowDocInfo((v) => !v)} aria-expanded={showDocInfo} aria-label="What counts as acceptable documentation?">
                <FaCircleQuestion aria-hidden="true" />
              </button>
            )}
          </p>
          {i === 1 && (
            <AnimatePresence>
              {showDocInfo && (
                <motion.div className="apf__doc-info-panel" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25, ease }}>
                  <p className="apf__doc-info-title">Any of the following are accepted:</p>
                  <ul className="apf__doc-info-list">
                    {ELIGIBILITY_DOC_TYPES.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          <label className="apf__dropzone">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="apf__file-input"
              onChange={(e) => {
                const f = e.target.files[0];
                if (f) {
                  set(`doc_${i}`, f.name);
                  set(`doc_file_${i}`, f);
                }
              }}
            />
            <FaFileArrowUp className="apf__dropzone-icon" aria-hidden="true" />
            <span className="apf__dropzone-text">
              {data[`doc_${i}`] ? (
                <>
                  <FaCheck aria-hidden="true" /> {data[`doc_${i}`]}
                </>
              ) : (
                <>
                  Drag & drop or <span>browse files</span>
                </>
              )}
            </span>
          </label>
        </div>
      ))}
    </div>
  );
}

/* ── Chapter 8 ── */
function Ch8Review({ formData, onEdit }) {
  const sections = [
    { key: "identity", label: "Personal Information", Icon: FaUser, fields: ["firstName", "lastName", "email", "phone", "city", "state", "country"] },
    { key: "service", label: "Service", Icon: FaShieldHalved, fields: ["group", "branch", "agency", "employment"] },
    { key: "logistics", label: "Logistics", Icon: FaPlane, fields: ["passport", "travelCity", "ecName", "ecPhone"] },
    { key: "availability", label: "Availability", Icon: FaCalendarDays, fields: ["season", "international", "room"] },
    { key: "story", label: "Your Story", Icon: FaPenToSquare, fields: ["why", "hope"] },
    { key: "documents", label: "Documents", Icon: FaFileArrowUp, fields: ["doc_0", "doc_1"] },
  ];
  const [checks, setChecks] = useState({ accurate: false, noGuarantee: false, privacy: false, terms: false });
  const allChecked = Object.values(checks).every(Boolean);
  return (
    <div className="apf__review">
      {sections.map((s) => (
        <div key={s.key} className="apf__review-section">
          <div className="apf__review-head">
            <s.Icon aria-hidden="true" />
            <span>{s.label}</span>
            <button type="button" className="apf__review-edit" onClick={() => onEdit(s.key)}>
              Edit
            </button>
          </div>
          <div className="apf__review-fields">
            {s.fields.map(
              (f) =>
                formData[s.key]?.[f] && (
                  <div key={f} className="apf__review-field">
                    <span className="apf__review-key">{f.replace(/([A-Z])/g, " $1").replace("_", " ")}</span>
                    <span className="apf__review-val">{formData[s.key][f]}</span>
                  </div>
                ),
            )}
          </div>
        </div>
      ))}
      <div className="apf__checks">
        {[
          ["accurate", "I certify that all information provided is accurate and complete."],
          ["noGuarantee", "I understand that submission does not guarantee acceptance."],
          ["privacy", "I agree to the Privacy Policy."],
          ["terms", "I agree to the Terms of Service."],
        ].map(([key, label]) => (
          <label key={key} className={`apf__check${checks[key] ? " apf__check--checked" : ""}`}>
            <input type="checkbox" checked={checks[key]} onChange={(e) => setChecks((p) => ({ ...p, [key]: e.target.checked }))} />
            <span className="apf__check-mark" aria-hidden="true">
              <FaCheck />
            </span>
            {label}
          </label>
        ))}
      </div>
      {!allChecked && <p className="apf__checks-warn">Please confirm all checkboxes above before submitting.</p>}
    </div>
  );
}

/* ── Success ── */
function SuccessScreen() {
  const TIMELINE = ["Application Received", "Verification", "Review", "Interview", "Preparation"];
  return (
    <motion.div className="apf__success" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
      <div className="apf__success-badge">
        <CircularBadge text="ONE JOURNEY · SAME PATH FORWARD ·" colors="var(--c-rust)" logoSize={36} />
      </div>
      <h2 className="apf__success-title u-display">
        Your Journey
        <br />
        Has Begun.
      </h2>
      <div className="apf__success-rule-row" aria-hidden="true">
        <span className="apf__success-rule" />
        <FaShieldHalved className="apf__success-mark" />
        <span className="apf__success-rule" />
      </div>
      <p className="apf__success-sub">
        Thank you for taking this step.
        <br />
        Your application has been received.
      </p>
      <div className="apf__success-timeline" role="list">
        {TIMELINE.map((step, i) => (
          <div key={i} className="apf__success-step" role="listitem">
            {i > 0 && <div className="apf__success-line" aria-hidden="true" />}
            <div className="apf__success-dot-wrap">
              <div className={`apf__success-dot${i === 0 ? " apf__success-dot--active" : ""}`} aria-hidden="true">
                {i === 0 && <FaCheck />}
              </div>
              <span className="apf__success-step-label">{step}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="apf__success-eta-wrap">
        <FaEnvelope className="apf__success-email-icon" aria-hidden="true" />
        <p className="apf__success-eta">
          We'll email you within
          <br />
          <strong>7–10 business days.</strong>
        </p>
      </div>
      {/* The application also created their portal account — send them there
          to follow their progress (sign-in is a code to the same email). */}
      <a href={localStorage.getItem("forge_portal_token") ? "/portal/dashboard" : "/portal/login"} className="apf__success-btn">
        Follow your progress in the portal <FaArrowRight aria-hidden="true" />
      </a>
      <a href="/" className="apf__success-home">Return to Home</a>
    </motion.div>
  );
}

/* ── Main wizard ── */
export default function ApplyForm() {
  const [chapter, setChapter] = useState(0);
  const [formData, setFormData] = useState({});
  const [returnToReview, setReturnToReview] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const crisis = formData?.eligibility?.suicidal === "yes" || formData?.eligibility?.addiction === "yes"; // eslint-disable-line no-unused-vars
  const topRef = useRef(null);
  const chapterKey = CHAPTERS[chapter]?.key;
  const progress = Math.round((chapter / TOTAL) * 100);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [chapterKey]: { ...(prev[chapterKey] || {}), [field]: value } }));
    if (fieldErrors[field])
      setFieldErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
  };
  const getData = () => formData[chapterKey] || {};

  const goTo = (idx) => {
    setChapter(idx);
    setFieldErrors({});
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const next = () => {
    if (chapter === TOTAL - 1) {
      handleSubmit();
      return;
    }
    const errors = getErrors(chapterKey, getData());
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setFieldErrors({});
    if (returnToReview) {
      setReturnToReview(false);
      goTo(TOTAL - 1);
    } else goTo(chapter + 1);
  };

  const prev = () => {
    if (chapter > 0) goTo(chapter - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("first_name", formData.identity?.firstName || "");
      fd.append("last_name", formData.identity?.lastName || "");
      fd.append("email", formData.identity?.email || "");
      fd.append("phone", formData.identity?.phone || "");
      fd.append("date_of_birth", formData.identity?.dob || "");
      fd.append("address", formData.identity?.address || "");
      fd.append("city", formData.identity?.city || "");
      fd.append("state", formData.identity?.state || "");
      fd.append("country", formData.identity?.country || "US");
      fd.append("category", formData.service?.group || "Veteran");
      fd.append("years_of_service", formData.service?.serviceDates || "");
      fd.append("eligibility_question_one", formData.eligibility?.suicidal || "no");
      fd.append("eligibility_question_two", formData.eligibility?.addiction || "no");
      const storyParts = [
        formData.story?.why ? `WHY I'M APPLYING:\n${formData.story.why}` : "",
        formData.story?.hope ? `\nWHAT I HOPE TO GAIN:\n${formData.story.hope}` : "",
        formData.story?.extra ? `\nADDITIONAL NOTES:\n${formData.story.extra}` : "",
      ].filter(Boolean);
      fd.append("story", storyParts.join("\n\n"));
      fd.append("preferred_time", formData.availability?.season || "");
      fd.append("international_travel", formData.availability?.international || "");
      fd.append("room_preference", formData.availability?.room || "");
      fd.append("limitations", formData.availability?.limitations || "");
      fd.append("passport_status", formData.logistics?.passport || "");
      fd.append("travel_city", formData.logistics?.travelCity || "");
      fd.append("ec_name", formData.logistics?.ecName || "");
      fd.append("ec_phone", formData.logistics?.ecPhone || "");
      fd.append("ec_relationship", formData.logistics?.ecRel || "");
      fd.append("branch", formData.service?.branch || "");
      fd.append("agency", formData.service?.agency || "");
      fd.append("employment_status", formData.service?.employment || "");
      if (formData.documents) {
        Object.keys(formData.documents).forEach((key) => {
          if (formData.documents[key] instanceof File) fd.append("documents", formData.documents[key]);
        });
      }
      const result = await applicationsAPI.submit(fd);
      // Check for specific error codes in the response body
      // (multipart errors come back as 4xx with a JSON body)
      if (result && result.success === false) {
        if (result.code === "EMAIL_BANNED") {
          setSubmitError("BANNED");
        } else if (result.code === "DUPLICATE_APPLICATION") {
          setSubmitError(`DUPLICATE:${result.status || "active"}`);
        } else {
          setSubmitError(result.message || "Submission failed. Please try again.");
        }
        return;
      }
      setSubmitted(true);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      // Also handle HTTP-level errors that throw
      const msg = err.message || "";
      if (msg.includes("EMAIL_BANNED") || msg.includes("banned")) {
        setSubmitError("BANNED");
      } else if (msg.includes("DUPLICATE_APPLICATION") || msg.includes("already exists")) {
        setSubmitError("DUPLICATE:active");
      } else {
        setSubmitError(msg || "Submission failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const editSection = (key) => {
    const idx = CHAPTERS.findIndex((c) => c.key === key);
    if (idx !== -1) {
      setReturnToReview(true);
      goTo(idx);
    }
  };

  if (submitted)
    return (
      <div className="apf__success-wrap container-fg container-2xxl">
        <SuccessScreen />
      </div>
    );

  const ch = CHAPTERS[chapter];
  const errorCount = Object.keys(fieldErrors).length;

  return (
    <section className="apf" ref={topRef} id="apply-form">
      <div className="apf__layout">
        <aside className="apf__sidebar">
          <p className="apf__sidebar-heading">Your Journey</p>
          <nav className="apf__sidebar-nav" aria-label="Application chapters">
            {CHAPTERS.map((c, i) => {
              const done = i < chapter,
                active = i === chapter;
              return (
                <button
                  key={c.key}
                  type="button"
                  className={`apf__sidebar-item${active ? " active" : ""}${done ? " done" : ""}`}
                  onClick={() => i <= chapter && goTo(i)}
                  aria-current={active ? "step" : undefined}
                >
                  <span className="apf__sidebar-num">{done ? <FaCheck /> : c.num}</span>
                  <div className="apf__sidebar-labels">
                    <span className="apf__sidebar-super">{c.label.toUpperCase()}</span>
                    <span className="apf__sidebar-sub">{c.sub}</span>
                  </div>
                  {i < TOTAL - 1 && <div className="apf__sidebar-connector" aria-hidden="true" />}
                </button>
              );
            })}
          </nav>
          <div className="apf__sidebar-help">
            <FaHeadphones className="apf__sidebar-help-icon" aria-hidden="true" />
            <div>
              <p className="apf__sidebar-help-title">Need Help?</p>
              <p className="apf__sidebar-help-sub">We're here for you.</p>
              <a href="mailto:contact@forge-retreats.org" className="apf__sidebar-help-link">
                contact@forge-retreats.org
              </a>
            </div>
          </div>
          <div className="apf__sidebar-badge">
            <CircularBadge text="ONE JOURNEY · SAME PATH FORWARD ·" colors="rgba(220,204,180,0.6)" logoSize={24} />
          </div>
        </aside>

        <div className="apf__main">
          <div className="apf__chapter-meta">
            <span className="apf__chapter-tag">
              Chapter {chapter + 1} of {TOTAL}
            </span>
            <div className="apf__progress-wrap">
              <span className="apf__progress-label">PROGRESS</span>
              <span className="apf__progress-pct">{progress}% Complete</span>
              <div className="apf__progress-bar">
                <div className="apf__progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>

          <div className="apf__chapter-head">
            <h2 className="apf__chapter-title u-display">{ch.label}</h2>
            <p className="apf__chapter-sub">
              {chapter === 0 && "Let's start with some basic information. All fields marked with * are required."}
              {chapter === 1 && "Tell us about your service background. This helps us match you with the right program."}
              {chapter === 2 && "Please answer both questions honestly. Your safety is our priority."}
              {chapter === 3 && "Help us understand your travel situation and who to contact in an emergency."}
              {chapter === 4 && "Let us know when you're available so we can plan accordingly."}
              {chapter === 5 && "This is the most important part. Take your time — there's no wrong answer."}
              {chapter === 6 && "Upload your verification documents. You can also email them after submitting."}
              {chapter === 7 && "Review your application before submitting. You can edit any section."}
            </p>
          </div>

          {/* Error summary banner */}
          <AnimatePresence>
            {errorCount > 0 && (
              <motion.div
                className="apf__error-banner"
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                <span className="apf__error-banner-icon">⚠</span>
                <div>
                  <strong>
                    Please fix {errorCount} field{errorCount > 1 ? "s" : ""} before continuing:
                  </strong>
                  <ul className="apf__error-list">
                    {Object.values(fieldErrors).map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {submitError && (
            <div className="apf__error-banner">
              <span className="apf__error-banner-icon">⚠</span>
              <span>{submitError}</span>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={chapter} className="apf__chapter-body" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease }}>
              {chapter === 0 && <Ch1Identity data={getData()} set={setField} errors={fieldErrors} />}
              {chapter === 1 && <Ch2Service data={getData()} set={setField} errors={fieldErrors} />}
              {chapter === 2 && <Ch3Eligibility data={getData()} set={setField} errors={fieldErrors} onCrisis={() => {}} />}
              {chapter === 3 && <Ch4Logistics data={getData()} set={setField} errors={fieldErrors} />}
              {chapter === 4 && <Ch5Availability data={getData()} set={setField} errors={fieldErrors} />}
              {chapter === 5 && <Ch6Story data={getData()} set={setField} errors={fieldErrors} />}
              {chapter === 6 && <Ch7Documents data={getData()} set={setField} group={formData.service?.group} />}
              {chapter === 7 && <Ch8Review formData={formData} onEdit={editSection} />}
            </motion.div>
          </AnimatePresence>

          <div className="apf__nav">
            <button
              type="button"
              className={`apf__nav-save${returnToReview ? " apf__nav-save--review" : ""}`}
              onClick={() => {
                if (returnToReview) {
                  setReturnToReview(false);
                  goTo(TOTAL - 1);
                } else console.log("save draft");
              }}
            >
              {returnToReview ? "← Back to Review" : "Save & Exit"}
            </button>
            <div className="apf__nav-right">
              {chapter > 0 && (
                <button type="button" className="apf__nav-back" onClick={prev}>
                  <FaArrowLeft aria-hidden="true" /> Back
                </button>
              )}
              <button type="button" className="apf__nav-next" onClick={next} disabled={submitting}>
                {chapter === TOTAL - 1 ? (submitting ? "Submitting…" : "Submit Application") : returnToReview ? "Save & Return to Review" : "Continue"}
                <FaArrowRight aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="apf__why">
            <img src="./assets/compassIllustration.png" alt="" className="apf__why-img" aria-hidden="true" />
            <div className="apf__why-content">
              <p className="apf__why-heading u-eyebrow">Why We Ask</p>
              <span className="apf__why-rule" aria-hidden="true" />
              <p className="apf__why-text">This information helps us understand who you are and ensure this experience is the right fit.</p>
              <div className="apf__why-points">
                {[
                  { Icon: FaShieldHalved, text: "Your information is secure and private." },
                  { Icon: FaUser, text: "It helps us create the best experience for you." },
                  { Icon: FaCheck, text: "This is just the beginning." },
                ].map(({ Icon, text }, i) => (
                  <div key={i} className="apf__why-point">
                    <Icon aria-hidden="true" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="apf__footer-band">
        <blockquote className="apf__footer-quote">
          <p>"Alone we can do so little. Together we can do so much."</p>
          <cite>— Helen Keller</cite>
        </blockquote>
        <div className="apf__footer-trust">
          <FaShieldHalved aria-hidden="true" />
          <p>Your application will be reviewed with care and respect. We're honored you're here.</p>
        </div>
        <div className="apf__footer-badge">
          <CircularBadge text="ONE JOURNEY · SAME PATH FORWARD ·" colors="var(--c-rust)" logoSize={28} />
        </div>
      </div>
    </section>
  );
}
